/**
 * lib/validations/schemas.ts
 * Zod schemas for all major Chalk forms.
 */

import { z } from 'zod'

// ─── Player ──────────────────────────────────────────────────────────────────

export const addPlayerSchema = z.object({
  full_name:      z.string().min(1, 'Name is required').max(100),
  jersey_number:  z.string().max(3).optional(),
  position:       z.string().max(50).optional(),
  date_of_birth:  z.string().optional(),
  guardian_email: z.string().email('Invalid email').optional().or(z.literal('')),
})

export type AddPlayerInput = z.infer<typeof addPlayerSchema>

// ─── Event ───────────────────────────────────────────────────────────────────

export const createEventSchema = z.object({
  title:     z.string().min(1, 'Title is required').max(120),
  type:      z.enum(['game', 'practice', 'tournament', 'other']),
  location:  z.string().max(200).optional(),
  starts_at: z.string().min(1, 'Start time is required'),
  ends_at:   z.string().optional(),
  opponent:  z.string().max(100).optional(),
  notes:     z.string().max(2000).optional(),
})

export type CreateEventInput = z.infer<typeof createEventSchema>

// ─── Payment ─────────────────────────────────────────────────────────────────

export const createPaymentSchema = z.object({
  label:       z.string().min(1, 'Label is required').max(120),
  amount:      z.coerce.number().positive('Amount must be positive').max(100000),
  due_date:    z.string().optional(),
  description: z.string().max(500).optional(),
})

export type CreatePaymentInput = z.infer<typeof createPaymentSchema>

// ─── Tournament ───────────────────────────────────────────────────────────────

export const createTournamentSchema = z.object({
  name:             z.string().min(1, 'Name is required').max(120),
  sport:            z.string().min(1, 'Sport is required'),
  start_date:       z.string().min(1, 'Start date is required'),
  end_date:         z.string().min(1, 'End date is required'),
  venue_name:       z.string().max(120).optional(),
  address:          z.string().max(200).optional(),
  city:             z.string().max(80).optional(),
  state:            z.string().max(2).optional(),
  entry_fee:        z.coerce.number().min(0).optional(),
  max_teams:        z.coerce.number().int().min(1).optional(),
  contact_email:    z.string().email('Invalid email').optional().or(z.literal('')),
  registration_url: z.string().url('Invalid URL').optional().or(z.literal('')),
  description:      z.string().max(2000).optional(),
  format:           z.string().max(80).optional(),
  awards:           z.string().max(200).optional(),
  hotel_url:        z.string().url('Invalid URL').optional().or(z.literal('')),
})

export type CreateTournamentInput = z.infer<typeof createTournamentSchema>

// ─── Tournament Division ──────────────────────────────────────────────────────

export const createTournamentDivisionSchema = z.object({
  name:         z.string().min(1, 'Division name is required').max(80),
  age_group:    z.string().max(20).optional(),
  max_teams:    z.coerce.number().int().min(1).optional(),
  entry_fee:    z.coerce.number().min(0).optional(),
  gender:       z.enum(['boys', 'girls', 'coed', 'open']).optional(),
})

export type CreateTournamentDivisionInput = z.infer<typeof createTournamentDivisionSchema>

// ─── Tryout Session ───────────────────────────────────────────────────────────

export const tryoutSessionSchema = z.object({
  title:          z.string().min(1, 'Title is required').max(120),
  starts_at:      z.string().min(1, 'Start time is required'),
  ends_at:        z.string().optional(),
  location:       z.string().max(200).optional(),
  max_registrants: z.coerce.number().int().min(1).optional(),
  entry_fee:      z.coerce.number().min(0).optional(),
  notes:          z.string().max(2000).optional(),
})

export type TryoutSessionInput = z.infer<typeof tryoutSessionSchema>

// ─── Payment Reminder ─────────────────────────────────────────────────────────

export const paymentReminderSchema = z.object({
  message:   z.string().min(1, 'Message is required').max(1000),
  send_push: z.boolean().default(true),
  send_email: z.boolean().default(true),
})

export type PaymentReminderInput = z.infer<typeof paymentReminderSchema>

// ─── Game / Score ─────────────────────────────────────────────────────────────

export const createGameSchema = z.object({
  title:      z.string().min(1, 'Title is required').max(120),
  opponent:   z.string().min(1, 'Opponent is required').max(100),
  home_away:  z.enum(['home', 'away']).default('home'),
  starts_at:  z.string().min(1, 'Start time is required'),
  location:   z.string().max(200).optional(),
})

export type CreateGameInput = z.infer<typeof createGameSchema>

// ─── Team ─────────────────────────────────────────────────────────────────────

export const createTeamSchema = z.object({
  name:      z.string().min(1, 'Team name is required').max(60, 'Team name must be 60 characters or less'),
  sport:     z.string().min(1, 'Sport is required'),
  season:    z.string().min(1, 'Season is required').max(40),
  age_group: z.string().max(40).optional().nullable(),
})

export type CreateTeamInput = z.infer<typeof createTeamSchema>

// ─── Payment request ─────────────────────────────────────────────────────────

export const createPaymentRequestSchema = z.object({
  title:       z.string().min(1, 'Title is required').max(120),
  description: z.string().max(500).optional().nullable(),
  due_date:    z.string().optional().nullable(),
})

export type CreatePaymentRequestInput = z.infer<typeof createPaymentRequestSchema>

// ─── Mark-as-Paid ─────────────────────────────────────────────────────────────

export const markAsPaidSchema = z.object({
  payment_request_id: z.string().uuid('Invalid payment request ID'),
  payer_id:           z.string().uuid('Invalid payer ID').optional(),
  player_guardian_id: z.string().uuid('Invalid guardian ID').optional(),
  payment_method:     z.enum(['cash', 'check', 'venmo', 'zelle', 'other']),
  amount_cents:       z.number().int().positive().optional(),
  notes:              z.string().max(500).optional().nullable(),
}).refine(
  (d) => (d.payer_id == null) !== (d.player_guardian_id == null),
  { message: 'Exactly one of payer_id or player_guardian_id is required' },
)

export type MarkAsPaidInput = z.infer<typeof markAsPaidSchema>

// ─── Auth — password recovery ─────────────────────────────────────────────────
// Task #142 (P1 AUTH). Shared by the web forgot/reset screens and mirrored
// (without zod) by the native equivalents. Supabase owns recovery-token
// single-use + expiry — these schemas only validate the form inputs.

export const resetPasswordRequestSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
})

export type ResetPasswordRequestInput = z.infer<typeof resetPasswordRequestSchema>

export const updatePasswordSchema = z
  .object({
    password:        z.string().min(8, 'Password must be at least 8 characters').max(72),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export type UpdatePasswordInput = z.infer<typeof updatePasswordSchema>
